import {
  html,
  css,
  OmniStyle,
  OmniTable,
  OmniSearch,
  OmniTooltip,
} from 'omni-ui';

OmniStyle.register();
OmniSearch.register();
OmniTooltip.register();

// Replacement of the one from omni-ui to sort different data types
// automatically, including numeric data, null, undefined, and strings.
// Maybe make this exportable in the future.
function sortObjects(arry, key, locale = 'en') {
  const val = row => (typeof key === 'function' ? key(row) : row[key]);
  return arry.slice().sort((a, b) => {
    const vala = val(a);
    const valb = val(b);
    // Treat null as largest, followed by undefined. Just need to pick a convention.
    if (vala === null && valb !== null) return 1;
    if (valb === null && vala !== null) return -1;
    if (vala === undefined && valb !== undefined) return 1;
    if (valb === undefined && vala !== undefined) return -1;
    // If both values are numbers, use numeric comparison, else string comparison
    return typeof vala === 'number' && typeof valb === 'number'
      ? vala - valb
      : String(vala).localeCompare(String(valb), locale, {
          sensitivity: 'base',
        });
  });
}

function sortTableData(arry, key, desc = false, locale = 'en') {
  const result = sortObjects(arry, key, locale);
  result.forEach((row, i) => {
    if (Array.isArray(row.nestedData)) {
      const rowCopy = { ...row };
      rowCopy.nestedData = sortTableData(row.nestedData, key, desc, locale);
      result[i] = rowCopy;
    }
  });
  return desc ? result.reverse() : result;
}

export class Om2Table extends OmniTable {
  static get properties() {
    return {
      autosort: {
        converter(value) {
          if (value === '') {
            return true;
          }
          if (value === 'nodefault') {
            return { key: null, dir: 'asc' };
          }
          if (value.includes(',')) {
            const [key, dir] = value.split(',');
            return { key, dir };
          }
          return { key: value, dir: 'asc' };
        },
      },
      autoTooltip: { type: Boolean },
      search: { type: Array },
      searchString: { type: String }, // internal
      headerless: { type: Boolean, reflect: true }, // internal
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .table-header {
          background-color: rgb(245, 248, 251);
          height: 3.2rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 0 0.6rem;
          border-radius: 10px 10px 0 0;
          border-bottom: 2px solid white;
          left: 0;
        }
        :host {
          border-radius: 10px;
        }
        .table-container {
          border-radius: 0 0 10px 10px;
        }
        :host([headerless]) .table-container {
          border-radius: 10px;
        }
        :host([shadowed]) {
          box-shadow: rgb(1 33 78 / 10%) 0px 10px 60px -5px;
        }
        :host([shadowed]) .table-header {
          border-bottom-color: #e8ecf3;
        }

        /* Unset styles in .omni table that break fixed positioning
         * https://annalect.atlassian.net/browse/OU-131
         */
        .omni .table,
        .omni .table tbody,
        .omni .table tr {
          transform-style: unset !important;
          transform: unset !important;
        }
      `,
    ];
  }

  static register(name = 'om2-table') {
    return super.register(name, Om2Table);
  }

  #superBuildRow;

  constructor() {
    super();
    this.headerless = true;
    this.addEventListener('sort', this.sortTable);

    // Workaround for change in omni-ui v7.7 which moved _buildRow() into the
    // constructor as an instance property so it can't be overriden using typical
    // prototype chain inheritance.
    // eslint-disable-next-line no-prototype-builtins
    if (this.hasOwnProperty('_buildRow')) {
      this.#superBuildRow = this._buildRow; // Save before changing instance property
      this._buildRow = this.constructor.prototype._buildRow.bind(this);
    } else {
      this.#superBuildRow = super._buildRow.bind(this);
    }
  }

  sortTable() {
    this.data = sortTableData(
      this.data,
      this.sort.key,
      this.sort.dir === 'desc'
    );
  }

  shouldUpdate(changedProperties) {
    if (
      (changedProperties.has('data') ||
        changedProperties.has('autosort') ||
        changedProperties.has('columns')) &&
      this.autosort &&
      this.data &&
      this.columns
    ) {
      // Initialize sort object to the first isMain isSortable column, or none (null)
      if (!this.sort?.key) {
        if (typeof this.autosort === 'object') {
          this.sort = this.autosort;
        } else {
          const key =
            this.columns?.find(d => d.isMain && d.isSortable)?.key ?? null;
          this.sort = { key, dir: 'asc' };
        }
      }
      // Sort data automatically
      this.sortTable();
    }
    if (changedProperties.has('search')) {
      this.checkIfHeaderless();
    }
    return super.shouldUpdate(changedProperties);
  }

  checkIfHeaderless() {
    const allSlots = Array.from(this.shadowRoot.querySelectorAll('slot'));
    const mySlots = allSlots.filter(slot => slot.name.startsWith('header'));
    const assignedSlots = mySlots.map(slot => slot.assignedElements()).flat();
    this.headerless = assignedSlots.length === 0 && !this.search;
  }

  renderRow(row, idx, level) {
    const normalize = x => String(x).toLowerCase();
    // Support nested keys (['parent', 'child', 'grandchild'])
    const getValue = key => {
      if (Array.isArray(key)) {
        let val = row;
        key.forEach(keyPart => {
          val = val?.[keyPart];
        });
        return val;
      }
      return row[key];
    };

    if (this.search && this.searchString) {
      if (
        !this.search.find(key =>
          normalize(getValue(key))?.includes(normalize(this.searchString))
        )
      )
        return '';
    }
    return super.renderRow?.(row, idx) ?? this.#superBuildRow(row, idx, level);
  }

  /**
   * Renamed version of renderRow() after omni-ui refactor of omni-table
   */
  _buildRow(row, idx, level) {
    return this.renderRow(row, idx, level);
  }

  /**
   * Mouseover event handler to dynamically add omni-tooltip elements on hover if
   * overflowing content is detected (and autoTooltip feature is enabled).
   *
   * @param {MouseEvent} e - The Mouseover event
   */
  _handleMouseover(e) {
    if (!this.autoTooltip) return;

    const { target } = e;
    const td = target.closest('td');
    if (!this.shadowRoot.contains(td)) return;

    // Do nothing if td already contains a tooltip or has more than one child
    if (td.querySelector('omni-tooltip')) return;
    if (td.childElementCount !== 1) return;

    // Look for span element that might overflow
    const span = td.querySelector('[part="td-content"],span');
    if (!span) return;

    const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } = span;
    const overflows = offsetWidth < scrollWidth || offsetHeight < scrollHeight;
    if (overflows) {
      // If overflow detected, add a tooltip element and move content into it.
      td.insertAdjacentHTML(
        'beforeend',
        `<omni-tooltip><div slot="content">${span.innerText}</div></omni-tooltip>`
      );

      td.firstElementChild.setAttribute('slot', 'invoker');
      const tooltip = td.lastElementChild;
      tooltip.prepend(td.firstElementChild);
      td.setAttribute('auto-tooltip', '');
      tooltip.setAttribute('auto-tooltip', '');

      // Trigger omni-tooltip to know the mouse is already over it
      setTimeout(() => {
        tooltip.dispatchEvent(new MouseEvent('mouseenter'));
      }, 100);
    }
  }

  /**
   * Mouseout event handler to dynamically remove omni-tooltip elements that were added
   * by the _handleMouseover event handler above. This restores the dom to its original
   * state when the mouse leaves.
   *
   * @param {MouseEvent} e - The Mouseout event
   */
  _handleMouseout(e) {
    if (!this.autoTooltip) return;

    const { target } = e;
    const td = target.closest('td[auto-tooltip]');
    if (!td || !this.shadowRoot.contains(td)) return;

    // Remove tooltip and move content back to td
    const tooltip = td.querySelector('omni-tooltip[auto-tooltip]');
    if (!tooltip) return;
    const parent = tooltip.parentElement;
    tooltip.firstElementChild.removeAttribute('slot');
    tooltip.firstElementChild.removeAttribute('aria-describedby');
    parent.insertBefore(tooltip.firstElementChild, tooltip);
    tooltip.remove();
    td.removeAttribute('auto-tooltip');
  }

  render() {
    const makeSlot = (name, isDefault = false) => html`
      <slot
        name="header-${name}"
        slot="${isDefault ? undefined : name}"
        @slotchange=${() => this.checkIfHeaderless()}
      ></slot>
    `;

    return html`
      <omni-style>
        <div
          class="is-flex is-flex-direction-column"
          @mouseover=${e => this._handleMouseover(e)}
          @mouseout=${e => this._handleMouseout(e)}
          @focus=${e => this._handleMouseover(e)}
          @blur=${e => this._handleMouseout(e)}
        >
          <omni-toolbar
            class="table-header ${this.headerless ? 'is-hidden' : ''}"
          >
            ${makeSlot('start')} ${makeSlot('center', true)}
            ${makeSlot('center-end')}
            <omni-search
              slot="end"
              ph="Search Table"
              class="${this.search ? '' : 'is-hidden'}"
              @search-update=${e => {
                this.searchString = e.detail.value;
              }}
            >
            </omni-search>
            ${makeSlot('end')}
          </omni-toolbar>
          <slot name="body"> ${super.render()} </slot>
        </div>
      </omni-style>
    `;
  }
}

Om2Table.register();
